import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, Locale } from "date-fns";
import { de, enUS, ja } from "date-fns/locale";
import { ClipboardList, History, Image as ImageIcon, Info, Link2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { DefectWithDetails } from "@/hooks/useDefectsQuery";
import { DefectStatus } from "@/types";
import { getProfileDisplayName } from "@/hooks/useAssignableProfiles";
import { SeverityBadge, DefectStatusBadge } from "@/components/ui/status-badge";
import { KeyFactsGrid, type KeyFact } from "@/components/ui/key-facts-grid";
import { DarkInfoPanel } from "@/components/ui/dark-info-panel";
import {
  DetailGrid,
  DetailGridAside,
  DetailGridMain,
  DetailSection,
} from "@/components/ui/detail-layout";
import { StatusTimeline } from "@/components/ui/status-timeline";
import { PhotoEvidenceGrid } from "@/components/defects/PhotoEvidenceGrid";
import { DefectTimelineWithCommentsSection } from "@/components/defects/DefectTimelineWithCommentsSection";
import { DefectSubtasksPanel } from "@/components/defects/DefectSubtasksPanel";
import { DefectActionsPanel } from "@/components/defects/DefectActionsPanel";
import { DefectTaskLinksCard } from "@/components/defects/DefectTaskLinksCard";
import { SapIntegrationCard } from "@/components/defects/SapIntegrationCard";
import { DefectSelfCorrectCard } from "@/components/defects/DefectSelfCorrectCard";

interface DefectDetailContentProps {
  defect: DefectWithDetails;
  /**
   * When true, hide write affordances inside the detail (comment form). Set for
   * fully read-only viewers — external partner_supplier / external_auditor and the
   * internal customer_approver read-only role (TES-626 / TES-565). Defaults to false
   * so internal users and customer_acceptance externals keep their comment form.
   */
  commentsReadOnly?: boolean;
}

/** Team-ID fuer das Unteraufgaben-Panel — nur fuer Top-Level-Maengel mit Team (sonst null). */
function subtaskTeamId(defect: DefectWithDetails): string | null {
  if (defect.parent_defect_id) return null;
  return defect.team_id ?? null;
}

type ProfileLike =
  | {
      first_name: string | null;
      last_name: string | null;
      display_name?: string | null;
      email: string;
    }
  | null
  | undefined;

/** Pixelgenaue Defect-Detail-Block-Ansicht (Stitch screen-07, F-CHK-MGM / TES-960).
 *
 * Reines Block-Layout-Re-Skin der bestehenden Detail-Daten:
 * - 12-col Grid: links col-8 (Kerndaten-Card + verknuepfte Items + Foto-Evidenz),
 *   rechts col-4 (DarkInfoPanel "System-Info" + Status-Lifecycle + Verlauf/Audit).
 * - Es werden reale `defects`-Spalten/Relationen aus `DefectWithDetails` angezeigt, inkl. der
 *   SAP-Integration (sap_sync_status/sap_qmeldung_nr, real seit Mig. 296/306; TES-545).
 *   KEINE erfundenen PT-TSK-/MAK-Felder, KEINE AI-Rail.
 * - Mobile (`grid-cols-1`) stapelt beide Spalten untereinander.
 */
export function DefectDetailContent({
  defect,
  commentsReadOnly = false,
}: DefectDetailContentProps) {
  return (
    <DetailGrid data-testid="defect-detail-block">
      {/* ── Left column: Core details ───────────────────────────────── */}
      <DetailGridMain>
        <DefectDetailMain defect={defect} />
      </DetailGridMain>

      {/* ── Right column: System info & history ─────────────────────── */}
      <DetailGridAside>
        <DefectDetailAside defect={defect} commentsReadOnly={commentsReadOnly} />
      </DetailGridAside>
    </DetailGrid>
  );
}

/** TES-2042: Linke Detail-Hauptspalte (Kennzahlen, Verknüpfungen, Unteraufgaben/Maßnahmen,
 * Fotos) als eigener Export — der `DefectBaseDialog` komponiert View- UND Edit-Modus in EINEM
 * `DetailGrid` (Aside bleibt über den Mode-Wechsel hinweg stabil gemountet, kein Remount des
 * Kommentar-Formulars). Fragment ohne Grid-Wrapper: der Aufrufer stellt die Spalte. */
export function DefectDetailMain({ defect }: { defect: DefectWithDetails }) {
  const { t, i18n } = useTranslation();
  const dateLocale: Locale = i18n.language === "ja" ? ja : i18n.language === "en" ? enUS : de;
  // MGM-18 (TES-481): Team-ID des Unteraufgaben-Panels einmal berechnen (nur Top-Level-Mängel).
  const subtasksTeamId = subtaskTeamId(defect);
  const profileName = (p: ProfileLike) => getProfileDisplayName(p, t("common.unknown"));
  // TES-1742: Fälligkeit als reines Datum (ohne Uhrzeit) für die Kennzahlen-Box.
  const fmtDate = useCallback(
    (iso: string) => format(new Date(iso), "d. MMM yyyy", { locale: dateLocale }),
    [dateLocale],
  );

  return (
    <>
      <MainInfoCard defect={defect} profileName={profileName} fmtDate={fmtDate} />
      <LinkedItemsCard defect={defect} />
      <DefectTaskLinksCard defectId={defect.id} />
      {/* MGM-18 (TES-481) + TES-1743: Unteraufgaben + Massnahmen — beide nur fuer Top-Level-Maengel
          (kein Subtask-im-Subtask), unter EINEM Team-Gate. */}
      {subtasksTeamId && (
        <>
          <DefectSubtasksPanel defectId={defect.id} teamId={subtasksTeamId} />
          <DefectActionsPanel defectId={defect.id} teamId={subtasksTeamId} />
        </>
      )}
      <PhotoEvidenceCard defect={defect} />
    </>
  );
}

/** TES-2042: Rechte Detail-Randspalte (System-Info, Status-Lifecycle, SAP, Activity-Log) als
 * eigener Export — der Edit-Modus des `DefectBaseDialog` rendert dieselben Blöcke neben dem
 * Formular, damit View UND Edit die identische große Detail-Familie (screen-07) sprechen.
 * Fragment ohne Grid-Wrapper: der Aufrufer stellt die Spalte (`DetailGridAside` bzw. der
 * `aside`-Slot von `DefectFormBody`) samt vertikalem Abstand. */
export function DefectDetailAside({ defect, commentsReadOnly = false }: DefectDetailContentProps) {
  const { t, i18n } = useTranslation();
  const dateLocale: Locale = i18n.language === "ja" ? ja : i18n.language === "en" ? enUS : de;
  const profileName = (p: ProfileLike) => getProfileDisplayName(p, t("common.unknown"));
  const fmt = useCallback(
    (iso: string) => format(new Date(iso), "d. MMM yyyy, HH:mm", { locale: dateLocale }),
    [dateLocale],
  );

  return (
    <>
      <SystemInfoPanel defect={defect} profileName={profileName} fmt={fmt} />
      <StatusLifecycleCard defect={defect} fmt={fmt} />
      {/* TES-2229 S8: „Ersteller & Storno" — sichtbare Selbstkorrektur-Entscheidungskette
          + Verdict (LH §L5.10; Hub-Muster). Write-Affordances nur ohne Read-Only-Kontext. */}
      {!commentsReadOnly && (
        <DefectSelfCorrectCard
          defect={{ id: defect.id, title: defect.title, status: defect.status }}
        />
      )}
      {/* TES-545: SAP-Integration (real sap_sync_status/sap_qmeldung_nr + push button). */}
      <SapIntegrationCard defect={defect} />
      <HistoryCard defect={defect} commentsReadOnly={commentsReadOnly} />
    </>
  );
}

/* ───────────────────────── Left column blocks ───────────────────────── */

// Deklarative Kennzahlen-Box: die zyklomatische Komplexität (9) stammt aus der flachen
// 6-Fakten-Liste mit Fallback-Ternaries — unter dem 15er-Threshold. Der CRAP-Ausschlag
// im CI ist der bekannte Coverage-Timeout (vgl. memory fallow-coverage-timeout); die 6
// Fakten sind durch DefectDetailContent.test.tsx abgedeckt.
// fallow-ignore-next-line complexity
function MainInfoCard({
  defect,
  profileName,
  fmtDate,
}: {
  defect: DefectWithDetails;
  profileName: (p: ProfileLike) => string;
  fmtDate: (iso: string) => string;
}) {
  const { t } = useTranslation();
  // TES-1742 Kennzahlen-Box: die 6 wichtigsten Mangel-Fakten prominent + konsistent
  // (Apple-Prinzip „wichtige Info vorgezogen"). Schweregrad/Status/Verifizierer wandern hierher
  // (zuvor verstreut: Severity-Badge oben rechts, Verifizierer im System-Panel) → weniger
  // Redundanz. Fälligkeit war bisher in der Detailansicht gar nicht sichtbar.
  const facts: KeyFact[] = [
    {
      id: "severity",
      label: t("defects.severity.label"),
      value: <SeverityBadge severity={defect.severity} />,
    },
    {
      id: "status",
      label: t("defects.detail.status"),
      value: (
        <span className="flex items-center gap-2">
          <StatusPulseDot status={defect.status} />
          <DefectStatusBadge status={defect.status} />
        </span>
      ),
    },
    {
      id: "reference",
      label: t("defects.dialog.reportReference"),
      value: defect.checklist_item?.title ?? defect.inspection?.checklists?.name ?? null,
    },
    {
      id: "assigned",
      label: t("dashboard.assignedTo"),
      value: defect.assigned_to_profile
        ? profileName(defect.assigned_to_profile)
        : t("defects.detail.unassigned"),
    },
    {
      id: "verifier",
      label: t("defects.verifyingQm"),
      value: defect.verifying_qm_profile ? profileName(defect.verifying_qm_profile) : null,
    },
    {
      id: "due",
      label: t("dashboard.dueDate"),
      value: defect.due_date ? fmtDate(defect.due_date) : null,
    },
  ];
  return (
    <DetailSection>
      <div className="mb-6 min-w-0">
        <h2 className="font-display text-lg font-semibold mb-1 break-words">{defect.title}</h2>
        {defect.description && (
          <p className="text-sm text-muted-foreground break-words">{defect.description}</p>
        )}
      </div>
      <KeyFactsGrid data-testid="defect-key-facts" facts={facts} />
    </DetailSection>
  );
}

/** Verknuepfte Items — reale Beziehungen (inspection / checklist_item) aus DefectWithDetails.
 * KEINE PT-TSK-Tabelle; bei fehlenden Relationen Empty-State mit identischer Tabellen-Optik. */
interface LinkedItemRow {
  id: string;
  type: string;
  name: string | null | undefined;
}

/** Pure builder for the linked-items rows (real inspection / checklist_item relations only).
 * Declarative (no branching) — candidate rows are filtered to those with a real name. */
function buildLinkedItemRows(
  defect: DefectWithDetails,
  t: Translate,
): { id: string; type: string; name: string }[] {
  const candidates: LinkedItemRow[] = [
    { id: "inspection", type: t("defects.inspection"), name: defect.inspection?.checklists?.name },
    { id: "checklist-item", type: t("defects.checklistItem"), name: defect.checklist_item?.title },
  ];
  return candidates.filter((row): row is { id: string; type: string; name: string } =>
    Boolean(row.name),
  );
}

function LinkedItemsCard({ defect }: { defect: DefectWithDetails }) {
  const { t } = useTranslation();
  const rows = useMemo(() => buildLinkedItemRows(defect, t), [defect, t]);

  return (
    <DetailSection
      layout="divided"
      icon={<Link2 className="h-5 w-5 text-primary" aria-hidden="true" />}
      title={t("defects.detail.linkedItems")}
    >
      {rows.length === 0 ? (
        <div className="p-6 flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
          <ClipboardList className="h-6 w-6" aria-hidden="true" />
          <p className="text-sm">{t("defects.detail.noLinkedItems")}</p>
        </div>
      ) : (
        <table className="w-full text-left">
          <thead className="bg-surface-container-low text-label-caps text-muted-foreground">
            <tr>
              <th className="px-4 py-3 border-b border-outline-variant font-bold">
                {t("defects.detail.linkType")}
              </th>
              <th className="px-4 py-3 border-b border-outline-variant font-bold">
                {t("defects.detail.linkName")}
              </th>
            </tr>
          </thead>
          <tbody className="text-data-tabular">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-surface-container-low transition-colors">
                <td className="px-4 py-3 border-b border-outline-variant whitespace-nowrap">
                  {row.type}
                </td>
                <td className="px-4 py-3 border-b border-outline-variant break-words">
                  {row.name}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DetailSection>
  );
}

function PhotoEvidenceCard({ defect }: { defect: DefectWithDetails }) {
  const { t } = useTranslation();
  const images = defect.images ?? [];
  return (
    <DetailSection
      icon={<ImageIcon className="h-5 w-5 text-primary" aria-hidden="true" />}
      title={t("defects.detail.photoEvidence")}
    >
      {images.length > 0 ? (
        <PhotoEvidenceGrid images={images} columns={4} />
      ) : (
        <div className="flex flex-col items-center justify-center text-center gap-2 py-8 text-muted-foreground">
          <ImageIcon className="h-6 w-6" aria-hidden="true" />
          <p className="text-sm">{t("defects.detail.noPhotos")}</p>
        </div>
      )}
    </DetailSection>
  );
}

/* ───────────────────────── Right column blocks ──────────────────────── */

/** "System-Info"-Dark-Panel (Stitch SAP-Sync-Optik) — gefuellt NUR mit realen Feldern.
 * KEINE Q-Meldung-ID, KEIN "Force SAP Refresh"-Backend; nur updated_at + verifying_qm_profile. */
function SystemInfoPanel({
  defect,
  profileName,
  fmt,
}: {
  defect: DefectWithDetails;
  profileName: (p: ProfileLike) => string;
  fmt: (iso: string) => string;
}) {
  const { t } = useTranslation();

  // TES-1742: Status + Verifizierer sind jetzt prominent in der Kennzahlen-Box (oben) →
  // hier entfernt (weniger Redundanz). Ersteller wandert hierher, damit die Info erhalten bleibt.
  const rows = [
    {
      id: "updated",
      label: t("defects.lastUpdated"),
      value: fmt(defect.updated_at),
    },
    {
      id: "created",
      label: t("defects.createdBy"),
      value: profileName(defect.created_by_profile),
    },
  ];

  return (
    <DarkInfoPanel
      data-testid="defect-system-info-panel"
      header={
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-secondary" aria-hidden="true" />
            <h3 className="font-display text-lg font-semibold">{t("defects.detail.systemInfo")}</h3>
          </div>
          <span className="rounded-full bg-success/20 px-2 py-0.5 text-xs font-semibold text-success">
            {t("defects.detail.live")}
          </span>
        </div>
      }
      rows={rows}
    />
  );
}

/** Status-Lifecycle-Timeline ueber den realen `defect_status` (open → in_progress → resolved → closed).
 * Reine Visualisierung des Status-Enums — kein erfundener Workflow. */
function StatusLifecycleCard({
  defect,
  fmt,
}: {
  defect: DefectWithDetails;
  fmt: (iso: string) => string;
}) {
  const { t } = useTranslation();
  const items = useStatusLifecycleItems(defect, fmt);

  return (
    <DetailSection title={t("defects.detail.statusLifecycle")}>
      <StatusTimeline items={items} />
    </DetailSection>
  );
}

/** Verlauf / Audit — realer Kommentar-/Event-Strom (bestehende Komponente). */
function HistoryCard({
  defect,
  commentsReadOnly,
}: {
  defect: DefectWithDetails;
  commentsReadOnly: boolean;
}) {
  const { t } = useTranslation();
  return (
    <DetailSection
      tone="muted"
      icon={<History className="h-5 w-5 text-primary" aria-hidden="true" />}
      title={t("defects.detail.activityLog")}
    >
      <DefectTimelineWithCommentsSection
        defectId={defect.id}
        defect={defect}
        readOnly={commentsReadOnly}
      />
    </DetailSection>
  );
}

/* ───────────────────────── Helpers ─────────────────────────────────── */

function StatusPulseDot({ status }: { status: DefectStatus }) {
  const tone: Record<DefectStatus, string> = {
    open: "bg-destructive animate-pulse",
    in_progress: "bg-info animate-pulse",
    resolved: "bg-success",
    closed: "bg-muted-foreground",
    cancelled: "bg-muted-foreground",
  };
  return <span className={cn("w-2 h-2 rounded-full shrink-0", tone[status])} aria-hidden="true" />;
}

type LifecycleState = "current" | "done" | "upcoming";

interface LifecycleItem {
  id: string;
  timestamp: string;
  label: string;
  detail?: string;
  state: LifecycleState;
}

// Linear lifecycle indices (real defect_status, no `verified`).
const LIFECYCLE_ORDER: DefectStatus[] = ["open", "in_progress", "resolved", "closed"];

/** Pure step-state resolver — keeps the lifecycle-builder's branching out of the useMemo body. */
function lifecycleStepState(idx: number, currentIdx: number, isCancelled: boolean): LifecycleState {
  if (isCancelled || idx > currentIdx) return "upcoming";
  return idx === currentIdx ? "current" : "done";
}

type Translate = (key: string) => string;

/** In-progress node timestamp: a real label once the step is reached, else "upcoming". */
function inProgressTimestamp(reached: boolean, t: Translate): string {
  return reached ? t("defects.status.inProgress") : t("defects.detail.upcoming");
}

/** Resolved node timestamp: the formatted resolved_at once reached, else "upcoming". */
function resolvedTimestamp(
  resolvedAt: string | null | undefined,
  reached: boolean,
  fmt: (iso: string) => string,
  t: Translate,
): string {
  const canShow = Boolean(resolvedAt) && reached;
  return canShow ? fmt(resolvedAt as string) : t("defects.detail.upcoming");
}

/** Closed node timestamp: the formatted updated_at once the closed step is reached, else "upcoming". */
function closedTimestamp(
  updatedAt: string | null | undefined,
  reached: boolean,
  fmt: (iso: string) => string,
  t: Translate,
): string {
  const canShow = Boolean(updatedAt) && reached;
  return canShow ? fmt(updatedAt as string) : t("defects.detail.upcoming");
}

/** Pure builder for the four linear lifecycle nodes (open → in_progress → resolved → closed). */
function buildLinearLifecycleItems(
  defect: DefectWithDetails,
  fmt: (iso: string) => string,
  t: Translate,
  step: (idx: number) => LifecycleState,
): LifecycleItem[] {
  const inProgressTs = inProgressTimestamp(step(1) !== "upcoming", t);
  const resolvedTs = resolvedTimestamp(defect.resolved_at, step(2) !== "upcoming", fmt, t);
  const closedTs = closedTimestamp(defect.updated_at, step(3) !== "upcoming", fmt, t);

  return [
    {
      id: "open",
      timestamp: fmt(defect.created_at),
      label: t("defects.status.open"),
      state: step(0),
    },
    {
      id: "in_progress",
      timestamp: inProgressTs,
      label: t("defects.status.inProgress"),
      state: step(1),
    },
    {
      id: "resolved",
      timestamp: resolvedTs,
      label: t("defects.status.resolved"),
      detail: defect.resolution_notes ?? undefined,
      state: step(2),
    },
    {
      id: "closed",
      timestamp: closedTs,
      label: t("defects.status.closed"),
      state: step(3),
    },
  ];
}

/** Pure cancelled-node builder (only appended when the defect is cancelled). */
function buildCancelledLifecycleItem(
  defect: DefectWithDetails,
  fmt: (iso: string) => string,
  t: Translate,
): LifecycleItem {
  return {
    id: "cancelled",
    timestamp: defect.cancelled_at ? fmt(defect.cancelled_at) : "",
    label: t("defects.status.cancelled"),
    detail: defect.cancellation_reason ?? undefined,
    state: "current",
  };
}

/** Map real defect_status + timestamps to current/done/upcoming lifecycle nodes. */
function useStatusLifecycleItems(
  defect: DefectWithDetails,
  fmt: (iso: string) => string,
): LifecycleItem[] {
  const { t } = useTranslation();
  return useMemo(() => {
    const isCancelled = defect.status === "cancelled";
    const currentIdx = isCancelled ? -1 : LIFECYCLE_ORDER.indexOf(defect.status);
    const step = (idx: number) => lifecycleStepState(idx, currentIdx, isCancelled);

    const items = buildLinearLifecycleItems(defect, fmt, t, step);
    return isCancelled ? [...items, buildCancelledLifecycleItem(defect, fmt, t)] : items;
  }, [defect, fmt, t]);
}
